import React from 'react';
import { merge } from 'lodash';
import { useTranslation } from 'react-i18next';
import PreferencesToggle from './PreferenceToggle';
import { CommunityMemberPermissions } from '../../../types/community';

export const getDefaultMemberPermissions = (value = false): CommunityMemberPermissions => ({
  MEMBERS: {
    CREATE: value,
    // REMOVE: value, // Admin only, not used currently
  },
  EVENTS: {
    CREATE: value,
    UPDATE: value,
    DELETE: value,
  },
  STATS_AND_SCORES: {
    CREATE: value,
    UPDATE: value,
    DELETE: value,
  },
  TASKS: {
    CREATE: value,
    UPDATE: value,
    DELETE: value,
  },
  PHOTO_ALBUMS: {
    CREATE: value,
    UPDATE: value,
    DELETE: value,
  },
  WALL: {
    CREATE: value,
    UPDATE: value,
    DELETE: value,
  },
});

interface MemberPreferenceFormProps {
  initialMemberPermissions?: CommunityMemberPermissions;
  onMemberPermissionsChange: (memberPermissions: CommunityMemberPermissions) => void;
  disableEventPreference?: boolean;
  disableScoreCardPreference?: boolean;
  disableTaskPreference?: boolean;
  disablePhotoAlbumPreference?: boolean;
  disableWallPreference?: boolean;
}

export default function MemberPreferenceForm({
  initialMemberPermissions,
  onMemberPermissionsChange,
  disableTaskPreference,
  disableEventPreference,
  disableScoreCardPreference,
  disablePhotoAlbumPreference,
  disableWallPreference,
}: MemberPreferenceFormProps): JSX.Element {
  const { t } = useTranslation();

  const [memberPermissions, setMemberPermissions] = React.useState<CommunityMemberPermissions>(
    merge(getDefaultMemberPermissions(), initialMemberPermissions) || getDefaultMemberPermissions(),
  );

  const handlePreferenceToggle = (category: keyof CommunityMemberPermissions, forceValue?: boolean) => {
    const previousSetting = Object.values(memberPermissions[category]!).includes(true);
    setMemberPermissions((prev) => ({
      ...prev,
      [category]: Object.entries(prev[category]!).reduce(
        (acc, [operation]) => ({ ...acc, [operation]: forceValue ?? !previousSetting }),
        {},
      ),
    }));
  };

  React.useEffect(() => {
    onMemberPermissionsChange(memberPermissions);
  }, [memberPermissions, onMemberPermissionsChange]);

  React.useEffect(() => {
    if (disableEventPreference) handlePreferenceToggle('EVENTS', false);
    if (disableTaskPreference) handlePreferenceToggle('TASKS', false);
    if (disableScoreCardPreference) handlePreferenceToggle('STATS_AND_SCORES', false);
    if (disablePhotoAlbumPreference) handlePreferenceToggle('PHOTO_ALBUMS', false);
    if (disableWallPreference) handlePreferenceToggle('WALL', false);
  }, [
    disableEventPreference,
    disableTaskPreference,
    disableScoreCardPreference,
    disablePhotoAlbumPreference,
    disableWallPreference,
  ]);

  const anySettingIsDisabled =
    disableEventPreference ||
    disableTaskPreference ||
    disableScoreCardPreference ||
    disablePhotoAlbumPreference ||
    disableWallPreference;

  const categories = Object.keys(getDefaultMemberPermissions());

  const isCategoryDisabled = (category: keyof CommunityMemberPermissions) =>
    category === 'EVENTS'
      ? disableEventPreference
      : category === 'STATS_AND_SCORES'
        ? disableScoreCardPreference
        : category === 'TASKS'
          ? disableTaskPreference
          : category === 'PHOTO_ALBUMS'
            ? disablePhotoAlbumPreference
            : category === 'WALL'
              ? disableWallPreference
              : false;

  return (
    <div className="space-y-4">
      {Object.entries(memberPermissions)
        .sort(([a], [b]) => categories.indexOf(a) - categories.indexOf(b))
        .map(([category, operations], index) => (
          <PreferencesToggle
            key={index}
            disabled={isCategoryDisabled(category as keyof CommunityMemberPermissions)}
            title={t(`page.onboarding.communityChooseSettingsScreen.preferences.${category}.title`)}
            subTitle={t(
              `page.onboarding.communityChooseSettingsScreen.preferences.${category}.description`,
            )}
            checked={Object.values(operations).includes(true)}
            onPress={() => handlePreferenceToggle(category as keyof CommunityMemberPermissions)}
          />
        ))}
      {anySettingIsDisabled && (
        <div className="h-4 italic">
          {t('page.onboarding.communityChooseSettingsScreen.unavailableSettings')}
        </div>
      )}
    </div>
  );
}
